import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { IconButton } from "@mui/material"
import { MdClose, MdContentCopy, MdDescription, MdRefresh } from "react-icons/md"
import { useModalProvider } from "../../../../Providers/ModalProvider"
import { useEndpointProvider } from "../../../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../../../Providers/BackendLinksProvider"
import {
    MobergButton,
    MobergButtonShape,
    MobergButtonVariant,
    MobergTheme,
} from "../../../../Moberg"


// While the job is IN_PROGRESS we re-fetch the log file at this cadence so the
// panel tails the worker's writes. Stops on terminal status / unmount.
const LIVE_POLL_MS = 2000

const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED", "CANCELLED", "DELETED"])


const Container = styled.div`
    width: 720px;
    max-width: 92vw;
    background: var(--color-surface-primary);
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`

const HeaderBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    background: var(--color-surface-panel);
    border-bottom: 1px solid var(--color-border-default);
    min-height: 64px;
`

const TitleBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
`

const TitleIconWrap = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: rgba(33, 150, 243, 0.12);
    color: var(--color-accent-interactive);
    flex-shrink: 0;
`

const TitleText = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`

const Title = styled.h2`
    margin: 0;
    font-family: Montserrat, sans-serif;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary-color);
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Subtitle = styled.span`
    font-size: 12px;
    color: var(--text-secondary-color);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Body = styled.div`
    padding: 20px 24px;
`

const ToolbarRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
    color: var(--text-secondary-color);
    font-size: 12px;
`

const LiveDot = styled.span`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #4CAF50;
    margin-right: 6px;
    animation: lm-live-pulse 1.6s ease-in-out infinite;

    @keyframes lm-live-pulse {
        0%, 100% { opacity: 1; }
        50%      { opacity: 0.35; }
    }
`

const RefreshButton = styled.button`
    background: transparent;
    border: 1px solid var(--color-border-default);
    border-radius: 999px;
    cursor: pointer;
    padding: 4px 10px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--text-primary-color);
    font-size: 11px;
    font-weight: 500;
    transition: background 0.15s ease, border-color 0.15s ease;
    &:hover {
        background: var(--color-hover-neutral);
        border-color: var(--color-accent-interactive);
        color: var(--color-accent-interactive);
    }
`

const LogPanel = styled.div`
    background: var(--color-surface-menu);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    padding: 14px 16px;
    height: 360px;
    /* Always show the track so the scrollbar is visible even on macOS overlay
       scrollbars (which otherwise only appear mid-scroll). */
    overflow-y: scroll;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    line-height: 1.55;
    color: var(--color-text-primary);
    white-space: pre-wrap;
    word-break: break-word;

    scrollbar-width: thin;
    scrollbar-color: var(--color-border-default) transparent;
    &::-webkit-scrollbar {
        width: 10px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: var(--color-border-default);
        border-radius: 5px;
        border: 2px solid var(--color-surface-menu);
    }
    &::-webkit-scrollbar-thumb:hover {
        background: var(--text-secondary-color);
    }
`

const FailureCallout = styled.div`
    background: rgba(244, 67, 54, 0.08);
    border: 1px solid rgba(244, 67, 54, 0.25);
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 12px;
    font-size: 13px;
    color: var(--color-text-primary);
    font-family: 'Source Sans Pro';

    & > strong {
        color: #F44336;
    }
`

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 36px 16px;
    color: var(--text-secondary-color);
    text-align: center;
`

const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary-color);
`

const EmptySub = styled.div`
    font-size: 12.5px;
    color: var(--text-secondary-color);
    max-width: 360px;
`

const FooterBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 24px;
    background: var(--color-surface-panel);
    border-top: 1px solid var(--color-border-default);
`


const LogsModal = ({ row }) => {
    const { close } = useModalProvider()
    const endpointProvider = useEndpointProvider()
    const { LINKS } = useBackendLinksProvider()

    const [logs, setLogs] = useState("")
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    // Live job status, seeded from the snapshot row and refreshed from each
    // /logs response. The row prop is captured once when the modal opens, so we
    // can't rely on it to know when the job finished — the endpoint reports the
    // current status so the tail can stop on a terminal state.
    const [jobStatus, setJobStatus] = useState(row?.status)
    const logPanelRef = useRef(null)
    const isMountedRef = useRef(true)
    // "Tail mode": follow new logs to the bottom. Starts true so the panel
    // opens scrolled to the latest line; flips off if the user scrolls up to
    // read older output, and back on when they scroll near the bottom again.
    const stickToBottomRef = useRef(true)

    const handleLogScroll = () => {
        const el = logPanelRef.current
        if (!el) return
        stickToBottomRef.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 40
    }

    const patientLabel = row?.patient_identifier || row?.patient_id || "—"
    const hasReason = row?.status === "FAILED" && !!row?.failure_reason
    // Tail only while the job is non-terminal. Driven by the live jobStatus
    // (refreshed from the endpoint), not the one-time row snapshot, so polling
    // actually stops once the job reaches COMPLETED/FAILED/CANCELLED/DELETED.
    const isLive = !TERMINAL_STATUSES.has(jobStatus)

    const fetchLogs = useCallback(async () => {
        if (!row?.id) return
        try {
            // Build the per-row endpoint inline — same pattern other per-row
            // routes use (cancel, delete, download_url all do this).
            const endpoint = {
                LINK: `${LINKS.DATA.EXPORTS.START.LINK}/${row.id}/logs`,
                OUTPUT_TYPE: "json",
                REQUIRES_TOKEN: true,
            }
            const result = await endpointProvider.get(endpoint)
            if (!isMountedRef.current) return
            setLogs(result?.logs ?? "")
            // Refresh status so the live-tail effect can stop once terminal.
            if (result?.status) setJobStatus(result.status)
            setError(null)
        } catch (err) {
            if (!isMountedRef.current) return
            setError(err?.message || err || "Failed to load logs.")
        } finally {
            if (isMountedRef.current) setLoading(false)
        }
    }, [row?.id, LINKS, endpointProvider])

    // Initial fetch on mount.
    useEffect(() => {
        isMountedRef.current = true
        fetchLogs()
        return () => { isMountedRef.current = false }
    }, [fetchLogs])

    // Live tail while IN_PROGRESS / PENDING. Stops once the row reaches a
    // terminal status (COMPLETED/FAILED/CANCELLED/DELETED) — that final fetch
    // already happened on the status transition above.
    useEffect(() => {
        if (!isLive) return undefined
        const interval = setInterval(fetchLogs, LIVE_POLL_MS)
        return () => clearInterval(interval)
    }, [isLive, fetchLogs])

    // Auto-scroll to the bottom on each log update (live `tail -f`). Stays
    // pinned to the latest line unless the user has scrolled up to read older
    // output (stickToBottomRef false). Runs on initial load too, so the panel
    // opens showing the newest logs instead of the top.
    useEffect(() => {
        const el = logPanelRef.current
        if (!el) return
        if (stickToBottomRef.current) el.scrollTop = el.scrollHeight
    }, [logs])

    const handleCopy = () => {
        const payload = logs || row?.failure_reason || ""
        if (!payload) return
        navigator.clipboard?.writeText(payload).catch(() => {})
    }

    const hasLogs = logs.length > 0
    const showEmpty = !loading && !hasLogs && !hasReason

    return (
        <Container>
            <HeaderBar>
                <TitleBlock>
                    <TitleIconWrap>
                        <MdDescription size={20} />
                    </TitleIconWrap>
                    <TitleText>
                        <Title>Logs · Job #{row?.id}</Title>
                        <Subtitle>Patient {patientLabel}</Subtitle>
                    </TitleText>
                </TitleBlock>
                <IconButton
                    onClick={close}
                    aria-label="Close"
                    sx={{ color: "var(--text-secondary-color)" }}
                >
                    <MdClose size={22} />
                </IconButton>
            </HeaderBar>

            <Body>
                {hasReason && (
                    <FailureCallout>
                        <strong>Failure reason:</strong> {row.failure_reason}
                    </FailureCallout>
                )}

                <ToolbarRow>
                    <span>
                        {isLive
                            ? (<><LiveDot /> Live · refreshes every {LIVE_POLL_MS / 1000}s</>)
                            : (row?.status === "DELETED"
                                ? "Job was deleted — log file removed."
                                : `Showing complete log for ${row?.status || "—"} job`)}
                    </span>
                    <RefreshButton type="button" onClick={fetchLogs} title="Refresh now">
                        <MdRefresh size={13} /> Refresh
                    </RefreshButton>
                </ToolbarRow>

                {showEmpty ? (
                    <EmptyState>
                        <EmptyTitle>No logs available</EmptyTitle>
                        <EmptySub>
                            This job has not produced any log output yet
                            {row?.status === "PENDING" ? " — waiting for the worker to claim it." : "."}
                        </EmptySub>
                    </EmptyState>
                ) : (
                    <LogPanel ref={logPanelRef} onScroll={handleLogScroll}>
                        {loading
                            ? "Loading logs…"
                            : error
                                ? `Couldn't load logs: ${error}`
                                : (hasLogs ? logs : "(empty)")}
                    </LogPanel>
                )}
            </Body>

            <FooterBar>
                {(hasLogs || hasReason) && (
                    <MobergButton
                        variant={MobergButtonVariant.OUTLINE}
                        theme={MobergTheme.BLUE}
                        shape={MobergButtonShape.RECTANGLE}
                        onClick={handleCopy}
                    >
                        <MdContentCopy size={16} /> Copy
                    </MobergButton>
                )}
                <MobergButton
                    variant={MobergButtonVariant.OUTLINE}
                    theme={MobergTheme.BLUE}
                    shape={MobergButtonShape.RECTANGLE}
                    onClick={close}
                >
                    Close
                </MobergButton>
            </FooterBar>
        </Container>
    )
}

export default LogsModal
